import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getBlogs } from "../../../api/blogs";
import { isBlogCoverFallback, resolveBlogCoverSrc } from "../../../utils/blogImages";
import { isBlogPublished } from "../../../utils/blogStatus";
import { localizeBlog } from "../../../utils/blogLocales";
import useLangLink from "../../../utils/useLangLink";
import BlogListLoader from "../../../components/Blog/BlogListLoader";
import "./Blog.css";
import SEO from "../../../utils/SEO";

function stripHtml(value) {
    return String(value || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function formatDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
}

export default function Blog() {
    const { t, i18n } = useTranslation();
    const lp = useLangLink();
    const tp = useCallback(
        (k, d) => t(`pages.blog.${k}`, d != null ? { defaultValue: d } : undefined),
        [t]
    );
    const lang = (i18n.language || "en").split("-")[0];

    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        const fetchBlogs = async () => {
            setLoading(true);
            setError("");
            try {
                const data = await getBlogs(lang);
                if (cancelled) return;
                const list = Array.isArray(data)
                    ? data.filter(isBlogPublished).map((blog) => localizeBlog(blog, lang))
                    : [];
                setBlogs(list);
            } catch (err) {
                if (cancelled) return;
                const msg =
                    err?.response?.data?.message ||
                    err?.message ||
                    tp("errorLoadingDesc", "Failed to load blog posts. Please try again.");
                setError(msg);
            } finally {
                if (cancelled) return;
                setLoading(false);
            }
        };

        fetchBlogs();
        return () => {
            cancelled = true;
        };
    }, [lang, tp]);

    return (
        <main className="main">
            <SEO
                title={tp("seo.title")}
                description={tp("seo.description")}
                keywords={tp("seo.keywords")}
                url="/blogs"
            />
            <div className="pbl-background">
                <div className="pbl-container">
                    <h2 className="pbl-title">{tp("title")}</h2>
                    {loading ? (
                        <BlogListLoader count={6} />
                    ) : error ? (
                        <div className="pbl-box">
                            <div
                                className="pbl-state-card pbl-state-card--error"
                                role="alert"
                                aria-label={tp("aria.listError")}
                            >
                                <h3 className="pbl-state-title">{tp("errorLoading")}</h3>
                                <p className="pbl-state-text">{error}</p>
                            </div>
                        </div>
                    ) : blogs.length === 0 ? (
                        <div className="pbl-box">
                            <div className="pbl-state-card">{tp("noBlogs")}</div>
                        </div>
                    ) : (
                        <div className="pbl-cards">
                            {blogs.map((blog) => {
                                const dateLabel = formatDate(blog?.createdAt);
                                const intro = blog?.intro || stripHtml(blog?.content || "");
                                const imageSrc = resolveBlogCoverSrc(blog?.image);
                                const coverIsFallback = isBlogCoverFallback(blog?.image);
                                const blogSlug = blog?.slug || blog?._id;
                                const title = blog?.title || tp("untitledPost");

                                return (
                                    <Link
                                        to={lp(`/blogs/${blogSlug}`)}
                                        className="pbl-item"
                                        key={blog?._id || blogSlug}
                                    >
                                        <div>
                                            <div className="pbl-image-wrap">
                                                <img
                                                    className={coverIsFallback ? "pbl-cover-fallback" : undefined}
                                                    src={imageSrc}
                                                    alt={
                                                        coverIsFallback
                                                            ? "Offshore Advisory"
                                                            : title || tp("coverAlt")
                                                    }
                                                />
                                            </div>
                                            <h3 className="pbl-item-title">{title}</h3>
                                            <div className="pbl-item-desc-wrap">
                                                <p className="pbl-item-desc">
                                                    {intro
                                                        ? `${intro.substring(0, 150)}${
                                                              intro.length > 150 ? "..." : ""
                                                          }`
                                                        : tp("readFullStory")}
                                                </p>
                                            </div>
                                            <div className="pbl-meta-row">
                                                <div className="pbl-item-date">{dateLabel}</div>
                                                <span className="pbl-read-more">{tp("readMore")}</span>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}
